'use client';

import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../../../lib/utils';
import { Container } from '../../container';

function ProductDetailsActionBarRoot({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

function ProductDetailsActionBarContainer({
  children,
  className,
  style,
}: { children: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <div
      className={cn(
        'max-sm:bg-transparent sm:border-y sm:border-y-theme-text/20',
        className
      )}
      style={style}
    >
      {children}
    </div>
  );
}

function ProductDetailsActionBarSection({ children }: { children: ReactNode }) {
  return (
    <Container asChild>
      <section className="flex min-h-[64px] items-center justify-between">
        {children}
      </section>
    </Container>
  );
}

function ProductDetailsActionBarLinks({ children }: { children: ReactNode }) {
  return (
    <nav className="flex-1 auto-cols-min grid-flow-col-dense gap-4 self-stretch max-lg:hidden lg:grid lg:gap-8">
      {children}
    </nav>
  );
}

function ProductDetailsActionBarImageContainer({
  children,
}: { children: ReactNode }) {
  return (
    <div className="relative block h-[50px] w-[50px] overflow-hidden rounded-theme-sm max-sm:hidden">
      {children}
    </div>
  );
}

function ProductDetailsActionBarMain({ children }: { children: ReactNode }) {
  return (
    <main className="block w-full auto-cols-min grid-flow-col-dense items-center justify-between gap-2 whitespace-nowrap py-2 max-sm:mx-auto sm:grid sm:w-auto sm:justify-center max-lg:sm:ml-auto md:gap-6">
      {children}
    </main>
  );
}

function ProductDetailsActionBarScrollToTopButton({
  title,
  details,
}: { title: string; details: string }) {
  return (
    <button
      type="button"
      className="max-sm:!hidden flex flex-col justify-center overflow-hidden whitespace-nowrap text-[--color]"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    >
      <span className="max-w-[100px] truncate font-semibold sm:max-w-[300px]">
        {title}
      </span>
      <span className="max-w-[100px] truncate text-xs sm:max-w-[300px]">
        {details}
      </span>
    </button>
  );
}

function ProductDetailsActionBarButtons({ children }: { children: ReactNode }) {
  return (
    <div className="grid w-full grid-cols-2 gap-2 sm:flex">{children}</div>
  );
}

ProductDetailsActionBarRoot.Container = ProductDetailsActionBarContainer;
ProductDetailsActionBarRoot.Section = ProductDetailsActionBarSection;
ProductDetailsActionBarRoot.Links = ProductDetailsActionBarLinks;
ProductDetailsActionBarRoot.ImageContainer =
  ProductDetailsActionBarImageContainer;
ProductDetailsActionBarRoot.Main = ProductDetailsActionBarMain;
ProductDetailsActionBarRoot.Buttons = ProductDetailsActionBarButtons;
ProductDetailsActionBarRoot.ScrollToTopButton =
  ProductDetailsActionBarScrollToTopButton;

export const ProductDetailsActionBar = ProductDetailsActionBarRoot;
